<script setup lang="ts">
import {ref, watch} from "vue";
import {ChevronLeft, Search, Send} from "@lucide/vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import BaseAlert from "@/core/components/base/BaseAlert.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import dateHelper from "@/core/helpers/dateHelper";
import formatHelper from "@/core/helpers/formatHelper";
import {birthdayGiftApi, customerApi} from "@/container";
import {BLOCKED_REASON_LABELS} from "@/birthday-gift/constants/palettes";
import {BirthdayGift} from "@/birthday-gift/interfaces";
import {Customer} from "@/customer/interfaces/Customer";

/**
 * Search any patient of the pharmacy and send them the birthday gift right
 * away, outside the daily listing (any patient, any day).
 */
const props = defineProps<{
    open: boolean;
    /** The active gifts offered as an explicit choice. */
    gifts: BirthdayGift[];
}>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    /** The message really left: the host refreshes the daily listing. */
    (e: 'sent'): void;
}>();

const keywords = ref('');
const searching = ref(false);
const results = ref<Customer[]>([]);
const selected = ref<Customer | null>(null);

/** 'auto' lets the backend match the gift on the patient profile. */
const giftChoice = ref('auto');

const sending = ref(false);
const outcome = ref<{type: 'success' | 'error', message: string} | null>(null);

let debounce: ReturnType<typeof setTimeout> | undefined;

watch(keywords, (value) => {
    clearTimeout(debounce);

    if (value.trim().length < 2) {
        results.value = [];
        searching.value = false;
        return;
    }

    searching.value = true;
    debounce = setTimeout(async () => {
        results.value = await customerApi().search(value.trim()).catch(() => []);
        searching.value = false;
    }, 300);
});

watch(() => props.open, (open) => {
    if (open) {
        keywords.value = '';
        results.value = [];
        selected.value = null;
        giftChoice.value = 'auto';
        outcome.value = null;
    }
});

const select = (customer: Customer) => {
    selected.value = customer;
    outcome.value = null;
};

const send = async () => {
    if (!selected.value || sending.value) {
        return;
    }

    sending.value = true;
    outcome.value = null;

    try {
        const voucher = await birthdayGiftApi().sendToCustomer(
            selected.value.id,
            giftChoice.value === 'auto' ? undefined : Number(giftChoice.value)
        );

        if (voucher.status === 'sent') {
            outcome.value = {
                type: 'success',
                message: `Message envoyé à ${voucher.customer.first_name} ${voucher.customer.last_name} `
                    + `avec le cadeau « ${voucher.gift?.name} ». Il apparaît dans le listing du jour.`,
            };
            emit('sent');
        } else {
            outcome.value = {
                type: 'error',
                message: voucher.blocked_reason
                    ? BLOCKED_REASON_LABELS[voucher.blocked_reason]
                    : 'Le message n\'est pas parti.',
            };
        }
    } catch (error) {
        const response = (error as {response?: {data?: {message?: string, errors?: Record<string, string[]>}}}).response;

        outcome.value = {
            type: 'error',
            message: response?.data?.message
                ?? Object.values(response?.data ?? {}).flat().find((value) => typeof value === 'string')
                ?? 'Le message n\'a pas pu être envoyé.',
        };
    } finally {
        sending.value = false;
    }
};
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="max-w-xl">
      <DialogHeader>
        <DialogTitle>Envoyer le cadeau à un patient</DialogTitle>
      </DialogHeader>

      <p class="text-xs leading-relaxed text-muted-foreground">
        Le patient recevra le message d'anniversaire et son QR code tout de suite, même si son
        anniversaire n'est pas aujourd'hui. Un seul cadeau par patient et par année.
      </p>

      <template v-if="!selected">
        <div>
          <Label>Rechercher un patient</Label>
          <div class="relative mt-1.5">
            <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              v-model="keywords"
              class="pl-9"
              placeholder="Nom, prénom…"
            />
          </div>
        </div>

        <BaseFlexSpinner v-if="searching" />
        <div
          v-else-if="results.length"
          class="max-h-64 divide-y divide-gray-light overflow-y-auto rounded-lg border border-gray-light"
        >
          <button
            v-for="customer in results"
            :key="customer.id"
            type="button"
            class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-primary-lightest"
            @click="select(customer)"
          >
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-semibold">{{ customer.information.name }}</span>
              <span class="block text-xs text-muted-foreground">
                Né(e) le {{ dateHelper.shortFormat(customer.information.birth_date) }}
                <template v-if="customer.information.phone">
                  · {{ formatHelper.readablePhone(customer.information.phone) }}
                </template>
              </span>
            </span>
          </button>
        </div>
        <p
          v-else-if="keywords.trim().length >= 2 && !searching"
          class="text-xs text-muted-foreground"
        >
          Aucun patient trouvé pour cette recherche.
        </p>
      </template>

      <template v-else>
        <div class="flex items-center gap-3 rounded-lg bg-primary-lightest px-3 py-2.5">
          <span class="min-w-0 flex-1">
            <span class="block text-sm font-semibold">{{ selected.information.name }}</span>
            <span class="block text-xs text-muted-foreground">
              Né(e) le {{ dateHelper.shortFormat(selected.information.birth_date) }}
              <template v-if="selected.information.phone">
                · {{ formatHelper.readablePhone(selected.information.phone) }}
              </template>
            </span>
          </span>
          <Button
            variant="link"
            class="px-0"
            @click="selected = null"
          >
            <ChevronLeft class="h-4 w-4" />Changer
          </Button>
        </div>

        <div>
          <Label>Le cadeau envoyé</Label>
          <Select v-model="giftChoice">
            <SelectTrigger class="mt-1.5 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">
                Selon le profil du patient
              </SelectItem>
              <SelectItem
                v-for="gift in gifts"
                :key="gift.id"
                :value="String(gift.id)"
              >
                {{ gift.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <BaseAlert
          v-if="outcome"
          :type="outcome.type"
          :title="outcome.type === 'success' ? 'Message envoyé' : 'Envoi impossible'"
        >
          {{ outcome.message }}
        </BaseAlert>

        <Button
          v-if="outcome?.type !== 'success'"
          class="self-end"
          :disabled="sending"
          @click="send"
        >
          <Send class="h-4 w-4" />{{ sending ? 'Envoi…' : 'Envoyer le cadeau maintenant' }}
        </Button>
        <Button
          v-else
          class="self-end border-solid"
          variant="outline"
          @click="emit('update:open', false)"
        >
          Fermer
        </Button>
      </template>
    </DialogContent>
  </Dialog>
</template>
